/**
 * Base API Service for Hilton Analytica COA Platform
 * 
 * Provides HTTP communication with Lambda endpoints with retry logic,
 * timeout handling, and error management.
 */

import {
  RequestOptions,
  DEFAULT_REQUEST_OPTIONS,
  LambdaResponse,
  LambdaErrorResponse,
} from '../types';

/**
 * Custom API error class with status code support
 */
export class ApiServiceError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ApiServiceError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Base API service for making HTTP requests to Lambda endpoints
 */
export class BaseApiService {
  private baseUrl: string;
  private defaultOptions: RequestOptions;

  constructor(baseUrl: string = '', options: RequestOptions = DEFAULT_REQUEST_OPTIONS) {
    this.baseUrl = baseUrl;
    this.defaultOptions = { ...DEFAULT_REQUEST_OPTIONS, ...options };
  }

  /**
   * Makes an HTTP GET request with retry logic and timeout
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | undefined>,
    options?: RequestOptions
  ): Promise<LambdaResponse<T>> {
    const mergedOptions = { ...this.defaultOptions, ...options };
    
    // Build URL with query parameters
    const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return this.request<T>('GET', url.toString(), undefined, mergedOptions);
  }

  /**
   * Makes an HTTP POST request with retry logic and timeout
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<LambdaResponse<T>> {
    const mergedOptions = { ...this.defaultOptions, ...options };
    const url = `${this.baseUrl}${endpoint}`;
    return this.request<T>('POST', url, body, mergedOptions);
  }

  /**
   * Core request method with retry logic and timeout
   */
  private async request<T>(
    method: string,
    url: string,
    body?: unknown,
    options: RequestOptions = this.defaultOptions
  ): Promise<LambdaResponse<T>> {
    const { timeout, retries, retryDelay, headers } = options;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= (retries ?? 3); attempt++) {
      try {
        const response = await this.executeRequest<T>(method, url, body, timeout, headers);
        return response;
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry on certain errors
        if (error instanceof ApiServiceError) {
          // Don't retry on 4xx errors (except 429)
          if (error.status && error.status >= 400 && error.status < 500 && error.status !== 429) {
            throw error;
          }
        }

        // Wait before retrying (if not last attempt)
        if (attempt < (retries ?? 3)) {
          await this.delay(retryDelay ?? 1000);
        }
      }
    }

    // All retries exhausted
    throw lastError;
  }

  /**
   * Executes a single HTTP request with timeout
   */
  private async executeRequest<T>(
    method: string,
    url: string,
    body?: unknown,
    timeout?: number,
    additionalHeaders?: Record<string, string>
  ): Promise<LambdaResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout ?? 30000);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...additionalHeaders,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type') || '';
      const responseText = await response.text();

      let data: LambdaResponse<T>;
      try {
        data = JSON.parse(responseText) as LambdaResponse<T>;
      } catch {
        if (contentType.includes('text/html')) {
          throw new ApiServiceError(
            'The server returned an unexpected response. Please try again later.',
            response.status,
            'INVALID_RESPONSE'
          );
        }
        throw new ApiServiceError(
          `Server error (${response.status}). Please try again later.`,
          response.status,
          'INVALID_RESPONSE'
        );
      }

      if (!response.ok) {
        const errorResponse = data as LambdaErrorResponse;
        throw new ApiServiceError(
          errorResponse.error || 'Request failed',
          response.status,
          errorResponse.code
        );
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiServiceError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiServiceError('Request timed out', 408, 'TIMEOUT');
        }
        throw new ApiServiceError(error.message, undefined, 'NETWORK_ERROR');
      }
      throw new ApiServiceError('Unknown error occurred', undefined, 'UNKNOWN');
    }
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export default instance
export const baseApiService = new BaseApiService();